import { useEffect, useState } from 'react';

/**
 * Background layer of the hero — 22 concentric "sonar" rings behind the
 * pack image (Figma 240:2905), pulsing slowly in/out as a single CSS
 * animation. Geometry and stroke-opacity values are lifted bit-for-bit
 * from the original `hero-sonar.json`, and the `@keyframes
 * hero-sonar-breath` rule in `index.css` reproduces the same group-level
 * scale + opacity sweep that the Lottie file performed at runtime — so
 * the visual is unchanged.
 *
 * The previous implementation embedded `lottie-react` + `lottie-web` and
 * fetched the JSON at mount, which (a) added ~80 KB gzip to the bundle
 * and (b) made the modern Pack page hang for the first second after
 * navigation, because the Lottie player initialisation and canvas
 * rasterisation competed with React's first paint and the pack image
 * loads. The new SVG version is plain DOM the moment React commits;
 * after that the browser only re-composites the layer each frame
 * (transform + opacity), with no per-frame paint cost.
 *
 * Reduced motion is honoured via `@media (prefers-reduced-motion: reduce)`
 * in `index.css`, which freezes the SVG at the bottom of the cycle
 * (scale 0.95, opacity 0.6). The Design Constructor preview iframe
 * (`?design_constructor=child`) also holds the static state to avoid
 * burning compositor cycles inside the constructor's static preview.
 */
export interface HeroBackdropProps {
  /**
   * Backend-resolved animation URL. Currently unused — every hero
   * variant we ship reduces to the same 22-ring breath, so the geometry
   * is baked in. Kept on the props shape so existing callers
   * (HeroSection, modern PackPage) don't have to change, and so a future
   * per-sport variant can plug in here without a callsite churn.
   */
  animationUrl?: string | null;
  /**
   * Renderer hint from the prior Lottie implementation. Ignored — the
   * SVG version has only one renderer. Kept on the props shape for API
   * compatibility.
   */
  renderer?: 'svg' | 'canvas';
}

/**
 * Ring geometry from `hero-sonar.json` — 22 concentric circles with
 * diameters stepping 32 → 620 in increments of 28, and white strokes
 * whose opacity fades from 0.32 (innermost) to 0.035 (outermost).
 */
const RINGS: ReadonlyArray<{ d: number; o: number }> = [
  { d: 32,  o: 0.32 },
  { d: 60,  o: 0.30 },
  { d: 88,  o: 0.28 },
  { d: 116, o: 0.26 },
  { d: 144, o: 0.24 },
  { d: 172, o: 0.22 },
  { d: 200, o: 0.20 },
  { d: 228, o: 0.18 },
  { d: 256, o: 0.16 },
  { d: 284, o: 0.14 },
  { d: 312, o: 0.13 },
  { d: 340, o: 0.12 },
  { d: 368, o: 0.11 },
  { d: 396, o: 0.10 },
  { d: 424, o: 0.09 },
  { d: 452, o: 0.08 },
  { d: 480, o: 0.07 },
  { d: 508, o: 0.06 },
  { d: 536, o: 0.05 },
  { d: 564, o: 0.045 },
  { d: 592, o: 0.04 },
  { d: 620, o: 0.035 },
];

function isConstructorPreview(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('design_constructor') === 'child';
}

export function HeroBackdrop(_props: HeroBackdropProps = {}): React.JSX.Element {
  // Constructor preview is detected once after mount — the constructor's
  // child iframe never navigates after load, so re-checking is wasted.
  // Starting `false` keeps SSR / first-paint output deterministic.
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (isConstructorPreview()) setPaused(true);
  }, []);

  return (
    <div
      data-hero-slot="backdrop"
      data-co-component="HeroBackdrop"
      aria-hidden
      className="pointer-events-none absolute inset-0"
    >
      <div
        className={
          'absolute inset-0 ' +
          (paused ? 'opacity-60 [transform:scale(0.95)]' : 'hero-sonar-breath')
        }
      >
        <svg
          viewBox="0 0 720 720"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden
          className="absolute inset-0 h-full w-full"
        >
          <g transform="translate(360 360)" fill="none" stroke="#fff">
            {RINGS.map((r) => (
              <circle
                key={r.d}
                cx={0}
                cy={0}
                r={r.d / 2}
                strokeWidth={1.1}
                strokeOpacity={r.o}
              />
            ))}
          </g>
        </svg>
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: 'var(--co-HeroBackdrop-tint)' }}
      />
    </div>
  );
}
